
/**
 * Global Search Service with Fuzzy Matching
 * Provides cross-module search across all entities in the application
 */

import type { Property, Tenant, Application, Bill, MaintenanceTicket, Vendor, Owner, Investor, Lease, Unit } from '@repo/shared-types';
import { formatCurrency } from '@repo/shared-utils';
import { ROUTES } from '@repo/shared-types/routeRegistry';

export interface SearchableEntity {
  id: string;
  type: 'property' | 'tenant' | 'application' | 'bill' | 'ticket' | 'vendor' | 'owner' | 'investor' | 'lease' | 'unit';
  title: string;
  subtitle?: string;
  description?: string;
  searchableText?: string; // Comprehensive searchable text for fuzzy matching
  metadata?: Record<string, unknown>;
  route: string;
  icon?: string;
  score?: number; // Relevance score for ranking
}

export interface SearchResult {
  entity: SearchableEntity;
  matches: Array<{
    field: string;
    value: string;
    score: number;
  }>;
  relevanceScore: number;
}

/**
 * Calculate Levenshtein distance between two strings
 */
function levenshteinDistance(str1: string, str2: string): number {
  const m = str1.length;
  const n = str2.length;
  const dp: number[][] = [];

  for (let i = 0; i <= m; i++) {
    dp[i] = [i];
  }
  for (let j = 0; j <= n; j++) {
    dp[0][j] = j;
  }

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = Math.min(
          dp[i - 1][j] + 1,     // deletion
          dp[i][j - 1] + 1,     // insertion
          dp[i - 1][j - 1] + 1  // substitution
        );
      }
    }
  }

  return dp[m][n];
}

/**
 * Calculate fuzzy match score (0-1, where 1 is perfect match)
 */
function fuzzyScore(query: string, text: string): number {
  if (!query || !text) return 0;
  
  const lowerQuery = query.toLowerCase();
  const lowerText = text.toLowerCase();
  
  // Exact match
  if (lowerText === lowerQuery) return 1.0;
  
  // Starts with query
  if (lowerText.startsWith(lowerQuery)) return 0.9;
  
  // Contains query
  if (lowerText.includes(lowerQuery)) return 0.7;
  
  // Word boundary match (query matches start of words)
  const words = lowerText.split(/\s+/);
  const queryWords = lowerQuery.split(/\s+/);
  let wordMatches = 0;
  for (const qWord of queryWords) {
    if (words.some(w => w.startsWith(qWord))) {
      wordMatches++;
    }
  }
  if (wordMatches > 0) {
    return 0.5 + (wordMatches / queryWords.length) * 0.2;
  }
  
  // Fuzzy match using Levenshtein distance
  const distance = levenshteinDistance(lowerQuery, lowerText);
  const maxLength = Math.max(lowerQuery.length, lowerText.length);
  if (maxLength === 0) return 0;
  
  const similarity = 1 - (distance / maxLength);
  return Math.max(0, similarity * 0.4); // Cap fuzzy matches at 0.4
}

/**
 * Find all matches in a text field
 */
function findMatches(query: string, text: string, fieldName: string): Array<{ field: string; value: string; score: number }> {
  if (!text) return [];
  
  const matches: Array<{ field: string; value: string; score: number }> = [];
  const score = fuzzyScore(query, text);
  
  if (score > 0.3) { // Only include matches above threshold
    matches.push({
      field: fieldName,
      value: text,
      score
    });
  }
  
  return matches;
}

/**
 * Search across multiple fields of an entity
 */
function searchEntity(query: string, entity: SearchableEntity): SearchResult | null {
  const matches: Array<{ field: string; value: string; score: number }> = [];
  
  // Search title (highest weight)
  const titleMatches = findMatches(query, entity.title, 'title');
  titleMatches.forEach(m => {
    m.score *= 1.5; // Boost title matches
    matches.push(m);
  });
  
  // Search subtitle
  if (entity.subtitle) {
    matches.push(...findMatches(query, entity.subtitle, 'subtitle'));
  }
  
  // Search description
  if (entity.description) {
    matches.push(...findMatches(query, entity.description, 'description'));
  }
  
  // Search metadata fields
  if (entity.metadata) {
    Object.entries(entity.metadata).forEach(([key, value]) => {
      if (typeof value === 'string') {
        matches.push(...findMatches(query, value, key));
      }
    });
  }
  
  if (matches.length === 0) return null;
  
  // Calculate overall relevance score
  const relevanceScore = matches.reduce((sum, m) => sum + m.score, 0) / matches.length;
  
  return {
    entity,
    matches,
    relevanceScore
  };
}

/**
 * Convert entities to searchable format
 */
export function prepareSearchableEntities(
  data: {
    properties?: Property[];
    tenants?: Tenant[];
    applications?: Application[];
    bills?: Bill[];
    tickets?: MaintenanceTicket[];
    vendors?: Vendor[];
    owners?: Owner[];
    investors?: Investor[];
    leases?: Lease[];
    units?: Unit[];
  },
  lookupData?: {
    properties?: Property[];
    units?: Unit[];
    tenants?: Tenant[];
    vendors?: Vendor[];
  }
): SearchableEntity[] {
  const properties = lookupData?.properties || data.properties || [];
  const units = lookupData?.units || data.units || [];
  const tenants = lookupData?.tenants || data.tenants || [];
  const vendors = lookupData?.vendors || data.vendors || [];
  const entities: SearchableEntity[] = [];
  
  // Properties
  if (data.properties) {
    data.properties.forEach((p: Property) => {
      entities.push({
        id: p.id,
        type: 'property',
        title: p.name,
        subtitle: p.address,
        description: `${p.type} • ${p.units} units • ${p.occupancyRate}% occupied`,
        metadata: {
          type: p.type,
          units: p.units?.toString(),
          occupancyRate: p.occupancyRate?.toString()
        },
        route: `/app/properties/${p.id}`
      });
    });
  }
  
  // Tenants
  if (data.tenants) {
    data.tenants.forEach((t: Tenant) => {
      const property = properties.find(p => p.id === t.propertyId);
      const unit = units.find(u => u.id === t.unitId);
      const propertyName = property?.name || 'Unknown';
      const unitDisplay = unit ? (unit.unitNumber || unit.id) : 'Unknown';
      entities.push({
        id: t.id,
        type: 'tenant',
        title: t.name,
        subtitle: `${propertyName} • Unit ${unitDisplay}`,
        description: t.email || t.phone || '',
        metadata: {
          email: t.email,
          phone: t.phone,
          property: propertyName,
          unit: unitDisplay,
          status: t.status
        },
        route: `/app/tenants/${t.id}`
      });
    });
  }
  
  // Applications
  if (data.applications) {
    data.applications.forEach((a: Application) => {
      // Build comprehensive searchable text from all application fields
      const searchableText = [
        a.applicantName,
        a.email,
        a.phone,
        a.propertyName,
        a.propertyId,
        a.unitNumber,
        a.status,
        a.employerName,
        a.currentAddress,
        a.previousAddress,
        a.reference1Name,
        a.reference2Name,
        a.coSignerName,
        a.source,
        a.score?.toString(),
        a.creditScore?.toString(),
        a.monthlyIncome?.toString(),
        a.ssnLast4,
        a.currentLandlordName,
        a.previousLandlordName,
        a.reference1Relationship,
        a.reference2Relationship,
        a.employmentDuration,
        a.rentalHistoryYears?.toString(),
        a.petTypes?.join(' '),
        a.petCount?.toString(),
      ].filter(Boolean).join(' ');

      const subProp = a.propertyName || a.propertyId || '';
      const subUnit = a.unitNumber ? ` • Unit ${a.unitNumber}` : '';
      entities.push({searchableText, id: a.id, type: 'application', title: a.applicantName, subtitle: `${subProp}${subUnit}`, description: `Status: ${a.status} | Score: ${a.score || 'N/A'}/100 | ${a.email || ''}${a.employerName ? ` | Employer: ${a.employerName}` : ''}`, metadata: {
          propertyId: a.propertyId,
          propertyName: a.propertyName,
          unitNumber: a.unitNumber,
          status: a.status,
          score: a.score?.toString(),
          creditScore: a.creditScore?.toString(),
          source: a.source,
          email: a.email,
          phone: a.phone,
          employerName: a.employerName,
          applicantName: a.applicantName,
          date: a.date,
          submittedAt: a.submittedAt
        }, route: `/app/leasing/applications?appId=${a.id}`});
    });
  }
  
  // Bills
  if (data.bills) {
    data.bills.forEach((b: Bill) => {
      const billVendor = vendors.find(v => v.id === b.vendorId);
      const vendorName = billVendor?.name || 'Unknown Vendor';
      entities.push({
        id: b.id,
        type: 'bill',
        title: `${vendorName} - ${b.invoiceNumber || b.id}`,
        subtitle: `${formatCurrency(b.amountCents ?? 0)} • ${b.status}`,
        description: b.category || '',
        metadata: {
          vendor: vendorName,
          invoiceNumber: b.invoiceNumber || b.id,
          amount: b.amountCents?.toString(),
          status: b.status,
          category: b.category
        },
        route: `${ROUTES.OPS_ACCOUNTING_BILLS}?billId=${b.id}`
      });
    });
  }
  
  // Maintenance Tickets
  if (data.tickets) {
    data.tickets.forEach((t: MaintenanceTicket) => {
      const ticketProperty = properties.find(p => p.id === t.propertyId);
      const ticketUnit = units.find(u => u.id === t.unitId);
      const ticketPropertyName = ticketProperty?.name || 'Unknown';
      const ticketUnitDisplay = ticketUnit ? (ticketUnit.unitNumber || ticketUnit.id) : 'N/A';
      entities.push({
        id: t.id,
        type: 'ticket',
        title: t.title,
        subtitle: `${ticketPropertyName} • Unit ${ticketUnitDisplay}`,
        description: t.description || '',
        metadata: {
          property: ticketPropertyName,
          unit: ticketUnitDisplay,
          status: t.status,
          priority: t.priority
        },
        route: `/app/maintenance?ticketId=${t.id}`
      });
    });
  }
  
  // Vendors
  if (data.vendors) {
    data.vendors.forEach((v: Vendor) => {
      entities.push({
        id: v.id,
        type: 'vendor',
        title: v.name,
        subtitle: v.trade || '',
        description: v.email || v.phone || '',
        metadata: {
          trade: v.trade,
          email: v.email,
          phone: v.phone,
          status: v.status
        },
        route: `/app/vendors/${v.id}`
      });
    });
  }
  
  // Owners
  if (data.owners) {
    data.owners.forEach((o: Owner) => {
      entities.push({
        id: o.id,
        type: 'owner',
        title: o.name,
        subtitle: o.email || o.phone || '',
        description: `${o.propertyIds?.length || 0} properties`,
        metadata: {
          email: o.email,
          phone: o.phone
        },
        route: `/app/owners/${o.id}`
      });
    });
  }
  
  // Investors
  if (data.investors) {
    data.investors.forEach((i: Investor) => {
      entities.push({
        id: i.id,
        type: 'investor',
        title: i.name,
        subtitle: i.email || '',
        description: `${formatCurrency(i.totalInvested ?? 0)} invested`,
        metadata: {
          email: i.email,
          totalInvested: i.totalInvested?.toString()
        },
        route: `/app/investors/${i.id}`
      });
    });
  }
  
  // Leases
  if (data.leases) {
    data.leases.forEach((l: Lease) => {
      const leaseTenant = tenants.find(t => t.id === l.tenantId);
      const leaseProperty = properties.find(p => p.id === l.propertyId);
      const leaseUnit = units.find(u => u.id === l.unitId);
      const tenantName = leaseTenant?.name || 'N/A';
      const propertyName = leaseProperty?.name || 'N/A';
      const unitDisplay = leaseUnit ? (leaseUnit.unitNumber || leaseUnit.id) : 'N/A';
      
      entities.push({
        id: l.id,
        type: 'lease',
        title: `Lease - ${tenantName}`,
        subtitle: `${propertyName} • Unit ${unitDisplay}`,
        description: `${l.startDate || 'N/A'} to ${l.endDate || 'N/A'} • ${formatCurrency(l.monthlyRent ?? 0)}/mo`,
        metadata: {
          tenantName,
          property: propertyName,
          unit: unitDisplay,
          status: l.status,
          startDate: l.startDate,
          endDate: l.endDate,
          monthlyRent: l.monthlyRent?.toString()
        },
        route: `/app/leasing/leases?leaseId=${l.id}`
      });
    });
  }
  
  // Units
  if (data.units) {
    data.units.forEach((u: Unit) => {
      const property = properties.find(p => p.id === u.propertyId);
      const tenant = tenants.find(t => t.unitId === u.id);
      const propertyName = property?.name || 'Unknown';
      const tenantName = tenant?.name || null;
      const unitDisplay = u.unitNumber || u.id;
      entities.push({
        id: u.id,
        type: 'unit',
        title: `Unit ${unitDisplay}`,
        subtitle: propertyName,
        description: tenantName || 'Vacant',
        metadata: {
          property: propertyName,
          tenant: tenantName,
          status: u.status
        },
        route: `/app/properties/${u.propertyId}/units/${u.id}`
      });
    });
  }
  
  return entities;
}

/**
 * Perform global search across all entities
 */
export function performGlobalSearch(
  query: string,
  entities: SearchableEntity[],
  options: {
    maxResults?: number;
    minScore?: number;
    typeFilter?: SearchableEntity['type'][];
  } = {}
): SearchResult[] {
  if (!query || query.trim().length < 2) {
    return [];
  }
  
  const { maxResults = 20, minScore = 0.3, typeFilter } = options;
  const trimmedQuery = query.trim();
  
  // Filter by type if specified
  let searchableEntities = entities;
  if (typeFilter && typeFilter.length > 0) {
    searchableEntities = entities.filter(e => typeFilter.includes(e.type));
  }
  
  // Search all entities
  const results: SearchResult[] = [];
  for (const entity of searchableEntities) {
    const result = searchEntity(trimmedQuery, entity);
    if (result && result.relevanceScore >= minScore) {
      results.push(result);
    }
  }
  
  // Sort by relevance score (highest first)
  results.sort((a, b) => b.relevanceScore - a.relevanceScore);
  
  // Limit results
  return results.slice(0, maxResults);
}

/**
 * Highlight matching text in a string
 */
export function highlightMatch(text: string, query: string): string {
  if (!query || !text) return text;

  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();

  // Find all occurrences
  const indices: number[] = [];
  let index = lowerText.indexOf(lowerQuery);
  while (index !== -1) {
    indices.push(index);
    index = lowerText.indexOf(lowerQuery, index + 1);
  }

  if (indices.length === 0) return text;

  // Build highlighted string
  let highlighted = '';
  let lastIndex = 0;

  for (const matchIndex of indices) {
    // Add text before match
    highlighted += text.substring(lastIndex, matchIndex);
    // Add highlighted match
    highlighted += `<mark class="bg-amber-200 dark:bg-amber-900/50 px-0.5 rounded">${text.substring(matchIndex, matchIndex + query.length)}</mark>`;
    lastIndex = matchIndex + query.length;
  }

  // Add remaining text
  highlighted += text.substring(lastIndex);

  return highlighted;
}

/**
 * Return segments for safe React rendering (no dangerouslySetInnerHTML).
 * Use for command palette / search result highlighting to avoid XSS.
 */
export function getHighlightSegments(text: string, query: string): Array<{ text: string; highlight: boolean }> {
  if (!query || !text) return [{ text, highlight: false }];

  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();

  const indices: number[] = [];
  let index = lowerText.indexOf(lowerQuery);
  while (index !== -1) {
    indices.push(index);
    index = lowerText.indexOf(lowerQuery, index + 1);
  }

  if (indices.length === 0) return [{ text, highlight: false }];

  const segments: Array<{ text: string; highlight: boolean }> = [];
  let lastIndex = 0;

  for (const matchIndex of indices) {
    if (matchIndex > lastIndex) {
      segments.push({ text: text.substring(lastIndex, matchIndex), highlight: false });
    }
    segments.push({
      text: text.substring(matchIndex, matchIndex + query.length),
      highlight: true,
    });
    lastIndex = matchIndex + query.length;
  }

  if (lastIndex < text.length) {
    segments.push({ text: text.substring(lastIndex), highlight: false });
  }

  return segments;
}

/**
 * Get search suggestions based on query
 */
export function getSearchSuggestions(
  query: string,
  entities: SearchableEntity[],
  maxSuggestions: number = 5
): string[] {
  if (!query || query.length < 2) return [];
  
  const suggestions = new Set<string>();
  const lowerQuery = query.toLowerCase();
  
  for (const entity of entities) {
    // Check title
    if (entity.title.toLowerCase().includes(lowerQuery)) {
      suggestions.add(entity.title);
    }
    
    // Check subtitle
    if (entity.subtitle?.toLowerCase().includes(lowerQuery)) {
      suggestions.add(entity.subtitle);
    }
    
    if (suggestions.size >= maxSuggestions) break;
  }
  
  return Array.from(suggestions).slice(0, maxSuggestions);
}

